import React, { useCallback, useEffect, useState } from "react";
import {
  MdShoppingCart, MdAttachMoney, MdStar, MdTrendingUp,
  MdAccessTime, MdRestaurantMenu, MdArrowForward,
  MdCheck,
} from "react-icons/md";
import { IoFastFood } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import Widget from "components/widget/Widget";
import Card from "components/card";
import { useAuth } from "contexts/AuthContext";
import { getOrders } from "services/ordersService";
import { getDashboardStats } from "services/statsService";

const STATUS_COLOR = {
  1: "bg-blue-100 text-blue-700",
  2: "bg-yellow-100 text-yellow-700",
  3: "bg-orange-100 text-orange-700",
  4: "bg-green-100 text-green-700",
  5: "bg-gray-100 text-gray-500",
  6: "bg-red-100 text-red-500",
};
const STATUS_LABEL = {
  1: "Nouveau", 2: "Confirmé", 3: "En préparation",
  4: "Prêt", 5: "Livré", 6: "Annulé",
};

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const unwrapData = (payload) => {
  let value = payload;

  while (
    value &&
    !Array.isArray(value) &&
    typeof value === "object" &&
    Object.prototype.hasOwnProperty.call(value, "data")
  ) {
    value = value.data;
  }

  return value;
};

const orderReference = (order) =>
  order?.ref || order?.reference || "Référence indisponible";

export default function Dashboard() {
  const { restaurantId, restaurant, user } = useAuth();
  const navigate = useNavigate();
  const [todayOrders, setTodayOrders] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const today = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

  const loadDashboard = useCallback(async ({ showLoader = false } = {}) => {
    if (!restaurantId) return;
    if (showLoader) setLoading(true);

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    const todayDate = formatLocalDate(new Date());

    const [ordersResult, statsResult] = await Promise.allSettled([
      getOrders(restaurantId, { orderDate: todayDate }),
      getDashboardStats(restaurantId, formatLocalDate(startOfMonth), todayDate),
    ]);

    if (ordersResult.status === "fulfilled") {
      const orders = unwrapData(ordersResult.value);
      setTodayOrders(Array.isArray(orders) ? orders : []);
    } else {
      console.error("[Dashboard] chargement des commandes impossible", ordersResult.reason);
    }

    if (statsResult.status === "fulfilled") {
      const stats = unwrapData(statsResult.value);
      setKpis(stats && typeof stats === "object" ? stats : null);
    } else {
      console.error("[Dashboard] chargement des statistiques impossible", statsResult.reason);
    }

    const failed = [ordersResult, statsResult].filter((result) => result.status === "rejected").length;
    setLoadError(
      failed === 2
        ? "Impossible de charger les commandes et les statistiques."
        : failed === 1
          ? "Certaines données n'ont pas pu être actualisées."
          : ""
    );
    setLoading(false);
  }, [restaurantId]);

  useEffect(() => {
    if (!restaurantId) return undefined;

    loadDashboard({ showLoader: true });

    const handleNewOrder = (event) => {
      if (Number(event.detail?.restaurant_id) !== Number(restaurantId)) return;
      loadDashboard();
    };
    const pollId = window.setInterval(() => loadDashboard(), 60000);

    window.addEventListener("restaurant:new-order", handleNewOrder);
    return () => {
      window.removeEventListener("restaurant:new-order", handleNewOrder);
      window.clearInterval(pollId);
    };
  }, [loadDashboard, restaurantId]);

  const performanceMetrics = kpis?.performance_metrics || {};
  const operationalMetrics = kpis?.operational_metrics || {};
  const averageRating = kpis?.restaurant?.rating ?? restaurant?.average_rating;
  const commissionRate = Math.min(
    100,
    Math.max(0, Number(kpis?.restaurant?.commission ?? restaurant?.commission ?? 0))
  );

  const newCount = todayOrders.filter((o) => o.status_id === 1).length;
  const activeCount = todayOrders.filter((o) => [2, 3].includes(o.status_id)).length;
  const deliveredToday = todayOrders.filter((o) => o.status_id === 5).length;
  const revenueToday = todayOrders
    .filter((o) => o.status_id === 5)
    .reduce((sum, order) => {
      const grossRestaurantRevenue = Number(
        order.orderCost_restaurant ?? order.orderCost_client ?? order.orderCost ?? 0
      );
      const orderCommissionRate = Math.min(
        100,
        Math.max(0, Number(order.commission ?? commissionRate))
      );

      return sum + (grossRestaurantRevenue * (100 - orderCommissionRate)) / 100;
    }, 0);
  const monthlyRestaurantRevenue =
    (Number(performanceMetrics.restaurant_share || 0) * (100 - commissionRate)) / 100;

  return (
    <div className="mt-3">
      {/* Welcome header */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
            <IoFastFood className="h-7 w-7 text-brand-500" />
            Bonjour, {restaurant?.name || user?.name || "Partenaire"} 👋
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 capitalize">{today}</p>
        </div>
        {newCount > 0 && (
          <button
            onClick={() => navigate("/admin/orders")}
            className="flex items-center gap-2 rounded-xl bg-brand-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-600 transition-colors animate-pulse"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-brand-500 text-xs font-extrabold">
              {newCount}
            </span>
            Nouvelle{newCount > 1 ? "s" : ""} commande{newCount > 1 ? "s" : ""} !
          </button>
        )}
      </div>

      {/* Today KPI widgets */}
      {loadError && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          {loadError}
        </div>
      )}
      <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Widget
          icon={<MdShoppingCart className="h-6 w-6" />}
          title="Nouvelles commandes"
          subtitle={String(newCount)}
        />
        <Widget
          icon={<MdAccessTime className="h-6 w-6" />}
          title="En cours"
          subtitle={String(activeCount)}
        />
        <Widget
          icon={<MdCheck className="h-6 w-6" />}
          title="Livrées aujourd'hui"
          subtitle={String(deliveredToday)}
        />
        <Widget
          icon={<MdAttachMoney className="h-6 w-6" />}
          title="Revenu net aujourd'hui"
          subtitle={`${revenueToday.toFixed(3)} TND`}
        />
      </div>

      {/* Monthly KPI row */}
      {kpis && (
        <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-3">
          <Card extra="p-4 flex items-center gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-50 dark:bg-navy-700">
              <MdTrendingUp className="h-6 w-6 text-brand-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Revenu net ce mois</p>
              <p className="text-xl font-bold text-navy-700 dark:text-white">
                {monthlyRestaurantRevenue.toFixed(3)} TND
              </p>
            </div>
          </Card>
          <Card extra="p-4 flex items-center gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-green-50 dark:bg-navy-700">
              <MdShoppingCart className="h-6 w-6 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Commandes ce mois</p>
              <p className="text-xl font-bold text-navy-700 dark:text-white">
                {operationalMetrics.total_orders || 0}
              </p>
            </div>
          </Card>
          <Card extra="p-4 flex items-center gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-amber-50 dark:bg-navy-700">
              <MdStar className="h-6 w-6 text-amber-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Note moyenne</p>
              <p className="text-xl font-bold text-navy-700 dark:text-white">
                {averageRating ? `${Number(averageRating).toFixed(1)} ★` : "—"}
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Quick actions */}
      <div className="mb-5 grid grid-cols-2 gap-4 md:grid-cols-4">
        {[
          { label: "Commandes", icon: MdShoppingCart, path: "/admin/orders", color: "bg-brand-50 text-brand-500" },
          { label: "Menu", icon: MdRestaurantMenu, path: "/admin/menu", color: "bg-orange-50 text-orange-500" },
          { label: "Horaires", icon: MdAccessTime, path: "/admin/schedule", color: "bg-green-50 text-green-500" },
          { label: "Statistiques", icon: MdTrendingUp, path: "/admin/stats", color: "bg-amber-50 text-amber-500" },
        ].map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-white dark:bg-navy-800 p-5 shadow-sm hover:shadow-md transition-shadow border border-gray-50 dark:border-navy-700"
          >
            <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${item.color}`}>
              <item.icon className="h-6 w-6" />
            </div>
            <span className="text-sm font-semibold text-navy-700 dark:text-white">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Recent orders */}
      <Card extra="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-navy-700 dark:text-white text-base">Commandes du jour</h3>
          <button
            onClick={() => navigate("/admin/orders")}
            className="flex items-center gap-1 text-xs font-semibold text-brand-500 hover:text-brand-600"
          >
            Voir tout <MdArrowForward className="h-4 w-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          </div>
        ) : todayOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <IoFastFood className="h-10 w-10 mb-2 opacity-30" />
            <p className="text-sm">Aucune commande aujourd'hui</p>
          </div>
        ) : (
          <div className="space-y-2">
            {todayOrders.slice(0, 6).map((order) => (
              <div
                key={order.id}
                className="flex items-center justify-between rounded-xl border border-gray-50 dark:border-navy-700 bg-gray-50 dark:bg-navy-700 px-4 py-2.5 cursor-pointer hover:bg-gray-100 dark:hover:bg-navy-600 transition-colors"
                onClick={() => navigate("/admin/orders")}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400">
                    #{orderReference(order)}
                  </span>
                  <span className="text-sm font-medium text-navy-700 dark:text-white">
                    {order.user?.first_name || order.user?.name || "Client"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-navy-700 dark:text-white">
                    {Number(order.total || 0) > 0
                      ? `${Number(order.total).toFixed(3)} TND`
                      : "—"}
                  </span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_COLOR[order.status_id] || "bg-gray-100 text-gray-500"}`}>
                    {STATUS_LABEL[order.status_id] || "—"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
